/**
 * @file ConsentTextsFieldList.tsx
 * @description Поля одной вкладки формы текстов согласия (phase19.16, Р3г;
 * uiux-r3g-consent-texts §3): раскладка — `CONSENT_FIELD_LAYOUT`, предел и
 * вставки — shared. Группы вкладки («Аналитика», «Режим «Уведомление»») —
 * подзаголовком. Общий контракт `texts` + `onChange(path, value)`: та же
 * разметка служит шторке «Смотреть» в режиме только чтения.
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import type { ReactNode } from "react";

import { consentFieldDomId, fieldsOfTab, type ConsentFieldLayout, type ConsentTextsTab } from "../../../lib/platform/consent-texts-fields";
import { consentFieldTakesPlaceholders, consentTextMax, getTextAt } from "../../../lib/platform/consent-texts-form";
import { PlaceholderTextarea } from "./PlaceholderTextarea";

interface Props {
  tab: ConsentTextsTab;
  kind: ConsentTextKind;
  texts: object;
  onChange?: (path: string, value: string) => void;
  errors?: ReadonlyMap<string, string>;
  warnings?: ReadonlyMap<string, string>;
  /** Подсвеченные поля («Отличия от ред. N» → «Показать»). */
  highlighted?: ReadonlySet<string>;
  baseNumber?: number;
  /** Подсказка «{categories} → …» у полей со вставками. */
  placeholderHint?: ReactNode;
  readOnly?: boolean;
  /** Префикс id: у шторки «Смотреть» свои id, чтобы не совпасть с формой. */
  idPrefix?: string;
}

/** Поля вкладки, сгруппированные подряд по `group`. */
function groupFields(fields: readonly ConsentFieldLayout[]): { group: string | undefined; fields: ConsentFieldLayout[] }[] {
  const out: { group: string | undefined; fields: ConsentFieldLayout[] }[] = [];
  for (const field of fields) {
    const last = out[out.length - 1];
    if (last && last.group === field.group) last.fields.push(field);
    else out.push({ group: field.group, fields: [field] });
  }
  return out;
}

export function ConsentTextsFieldList(props: Props) {
  const { tab, kind, texts, onChange, errors, warnings, highlighted, baseNumber, placeholderHint, readOnly, idPrefix = "" } = props;
  const groups = groupFields(fieldsOfTab(tab));

  return (
    <div className="flex min-w-0 flex-col gap-5">
      {groups.map(({ group, fields }) => (
        <div key={group ?? "main"} role={group ? "group" : undefined} aria-label={group} className="flex min-w-0 flex-col gap-4">
          {group ? <h3 className="type-body m-0 font-semibold text-on-surface">{group}</h3> : null}
          {fields.map((field) => {
            const takes = consentFieldTakesPlaceholders(kind, field.path);
            return (
              <PlaceholderTextarea
                key={field.path}
                id={`${idPrefix}${consentFieldDomId(field.path)}`}
                label={field.label}
                hint={field.hint}
                value={getTextAt(texts, field.path)}
                onChange={(value) => onChange?.(field.path, value)}
                max={consentTextMax(kind, field.path)}
                error={errors?.get(field.path)}
                warning={warnings?.get(field.path)}
                placeholders={takes}
                placeholderHint={takes ? placeholderHint : undefined}
                multiline={field.multiline}
                changedNote={highlighted?.has(field.path) ? `Изменено от ред. ${baseNumber ?? ""}` : undefined}
                readOnly={readOnly}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
